import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Footer from '../home/components/Footer';
import TradeInEstimator from './components/TradeInEstimator';
import { useWishlist } from '../../hooks/useWishlist';

interface VehicleImage {
  url: string;
  alt: string;
}

interface VehicleData {
  id: number;
  name: string;
  price: number;
  mileage: string;
  location: string;
  badge: string;
  images: string[];
  year?: string;
  make?: string;
  model?: string;
  trim?: string;
  vin?: string;
  stockNumber?: string;
  exteriorColor?: string;
  interiorColor?: string;
  transmission?: string;
  drivetrain?: string;
  fuelType?: string;
  engine?: string;
  mpgCity?: string;
  mpgHighway?: string;
  features?: string[];
  description?: string;
}

export default function VehicleDetailsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState<VehicleData | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const { isInWishlist, toggleWishlist } = useWishlist();

  // Financing calculator state
  const [downPayment, setDownPayment] = useState(5000);
  const [interestRate, setInterestRate] = useState(5.9);
  const [loanTerm, setLoanTerm] = useState(60);
  const [monthlyPayment, setMonthlyPayment] = useState(0);

  const handleCompareClick = () => {
    if (vehicle) {
      navigate('/compare', { state: { vehicles: [vehicle] } });
    }
  };

  const handleWishlistToggle = () => {
    if (vehicle) {
      toggleWishlist({
        id: vehicle.id,
        name: vehicle.name,
        price: vehicle.price,
        mileage: vehicle.mileage,
        location: vehicle.location,
        badge: vehicle.badge,
        images: vehicle.images
      });
    }
  };

  // -------------------------------------------------------------------------
  // Load vehicle data (from router state or fallback to localStorage)
  // -------------------------------------------------------------------------
  useEffect(() => {
    const vehicleData = location.state?.vehicle;

    if (vehicleData) {
      // Check localStorage for updated vehicle data
      const savedVehicles = localStorage.getItem('vehicles');
      let finalVehicleData = vehicleData;
      
      if (savedVehicles) {
        const allVehicles = JSON.parse(savedVehicles);
        const updatedVehicle = allVehicles.find((v: VehicleData) => v.id === vehicleData.id);
        if (updatedVehicle) {
          // Use updated images from localStorage
          finalVehicleData = { ...vehicleData, images: updatedVehicle.images };
        }
      }

      // Override price for 2018 Infiniti QX60
      if (finalVehicleData.name === '2018 Infiniti QX60') {
        finalVehicleData = { ...finalVehicleData, price: 12495 };
      }

      // Enhance vehicle data with synthetic details (for demo purposes)
      const nameParts = finalVehicleData.name.split(' ');
      const enhancedVehicle: VehicleData = {
        ...finalVehicleData,
        year: nameParts[0],
        make: nameParts[1],
        model: nameParts.slice(2, -1).join(' '),
        trim: nameParts.slice(-1)[0],
        vin: `1HGBH41JXMN${Math.floor(Math.random() * 1_000_000)
          .toString()
          .padStart(6, '0')}`,
        stockNumber: `STK${finalVehicleData.id.toString().padStart(5, '0')}`,
        exteriorColor: ['White', 'Black', 'Silver', 'Blue', 'Red'][
          Math.floor(Math.random() * 5)
        ],
        interiorColor: ['Black', 'Gray', 'Beige', 'Brown'][
          Math.floor(Math.random() * 4)
        ],
        transmission: 'Automatic',
        drivetrain: ['FWD', 'AWD', '4WD', 'RWD'][
          Math.floor(Math.random() * 4)
        ],
        fuelType: 'Gasoline',
        engine:
          finalVehicleData.name.includes('F-150') ||
          finalVehicleData.name.includes('Pilot')
            ? '3.5L V6'
            : '2.5L 4-Cylinder',
        mpgCity: '22',
        mpgHighway: '28',
        features: [
          'Backup Camera',
          'Bluetooth Connectivity',
          'Cruise Control',
          'Power Windows',
          'Power Locks',
          'Air Conditioning',
          'Alloy Wheels',
          'Keyless Entry',
          'Traction Control',
          'Stability Control',
          'ABS Brakes',
          'Multiple Airbags',
          'AM/FM Stereo',
          'USB Port',
          'Auxiliary Audio Input',
          'Tilt Steering Wheel',
        ],
        description: `This ${finalVehicleData.name} is in excellent condition and has been thoroughly inspected by our certified technicians. With only ${finalVehicleData.mileage} miles, this vehicle offers great value and reliability. It comes with a clean title and comprehensive vehicle history report. Our dealership stands behind every vehicle we sell with our quality guarantee. Schedule a test drive today and experience this amazing vehicle for yourself!`,
      };

      setVehicle(enhancedVehicle);
    } else {
      // No data – navigate back to the homepage
      navigate('/');
    }
  }, [location.state, navigate]);

  // Listen for vehicle updates from admin panel
  useEffect(() => {
    const handleVehiclesUpdate = () => {
      if (vehicle) {
        const savedVehicles = localStorage.getItem('vehicles');
        if (savedVehicles) {
          const allVehicles = JSON.parse(savedVehicles);
          const updatedVehicle = allVehicles.find((v: VehicleData) => v.id === vehicle.id);
          if (updatedVehicle && updatedVehicle.images) {
            // Update only the images
            setVehicle((prev) => prev ? { ...prev, images: updatedVehicle.images } : null);
            // Reset to first image if current index is out of bounds
            if (currentImageIndex >= updatedVehicle.images.length) {
              setCurrentImageIndex(0);
            }
          }
        }
      }
    };

    window.addEventListener('vehiclesUpdated', handleVehiclesUpdate);
    return () => window.removeEventListener('vehiclesUpdated', handleVehiclesUpdate);
  }, [vehicle, currentImageIndex]);

  // Calculate monthly payment
  useEffect(() => {
    if (vehicle) {
      const principal = vehicle.price - downPayment;
      const monthlyRate = interestRate / 100 / 12;
      const numPayments = loanTerm;

      if (monthlyRate === 0) {
        setMonthlyPayment(principal / numPayments);
      } else {
        const payment =
          (principal * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
          (Math.pow(1 + monthlyRate, numPayments) - 1);
        setMonthlyPayment(payment);
      }
    }
  }, [vehicle, downPayment, interestRate, loanTerm]);

  // -------------------------------------------------------------------------
  // Lightbox escape‑key handling
  // -------------------------------------------------------------------------
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && lightboxOpen) {
        setLightboxOpen(false);
        document.body.style.overflow = 'auto';
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [lightboxOpen]);

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  const lightboxPrevImage = () => {
    if (vehicle) {
      setCurrentImageIndex(
        (prev) => (prev - 1 + vehicle.images.length) % vehicle.images.length
      );
    }
  };

  const lightboxNextImage = () => {
    if (vehicle) {
      setCurrentImageIndex((prev) => (prev + 1) % vehicle.images.length);
    }
  };

  // -------------------------------------------------------------------------
  // Render loading state
  // -------------------------------------------------------------------------
  if (!vehicle) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-[#2563EB] animate-spin"></i>
          <p className="mt-4 text-gray-600">Loading vehicle details...</p>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Main component markup
  // -------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/inventory')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl"></i>
            <span className="font-medium">Back to Inventory</span>
          </button>
          <div className="flex items-center gap-4">
            <button 
              onClick={handleCompareClick}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
              title="Compare Vehicles"
            >
              <i className="ri-scales-line text-xl text-gray-600"></i>
            </button>
            <button className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer">
              <i className="ri-share-line text-xl text-gray-600"></i>
            </button>
            <button 
              onClick={handleWishlistToggle}
              className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                isInWishlist(vehicle?.id || 0)
                  ? 'bg-red-50 hover:bg-red-100'
                  : 'hover:bg-gray-100'
              }`}
              title={isInWishlist(vehicle?.id || 0) ? 'Remove from wishlist' : 'Add to wishlist'}
            >
              <i className={`text-xl ${
                isInWishlist(vehicle?.id || 0)
                  ? 'ri-heart-fill text-red-500'
                  : 'ri-heart-line text-gray-600'
              }`}></i>
            </button>
          </div>
        </div>
      </header>

      {/* Image Gallery */}
      <section className="bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-3">
              <div
                className="relative w-full h-[500px] bg-white rounded-2xl overflow-hidden group cursor-pointer"
                onClick={() => openLightbox(currentImageIndex)}
              >
                <img
                  src={vehicle.images[currentImageIndex]}
                  alt={`${vehicle.name} - Main view`}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-4 left-4 bg-[#2563EB] text-white px-4 py-2 rounded-full text-sm font-medium">
                  {vehicle.badge}
                </div>
                <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm">
                  {currentImageIndex + 1} / {vehicle.images.length}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              {vehicle.images.slice(0, 3).map((img, index) => (
                <div
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`relative w-full h-[156px] bg-white rounded-xl overflow-hidden cursor-pointer transition-all ${
                    currentImageIndex === index
                      ? 'ring-4 ring-[#2563EB]'
                      : 'hover:ring-2 ring-gray-300'
                  }`}
                >
                  <img
                    src={img}
                    alt={`${vehicle.name} - Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Vehicle Info */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-3 gap-12">
            {/* Left Column - Details */}
            <div className="col-span-2">
              <div className="mb-8">
                <h1 className="text-4xl font-bold text-gray-900 mb-2">
                  {vehicle.name}
                </h1>
                <p className="text-lg text-gray-600">
                  {vehicle.mileage} miles • {vehicle.location}
                </p>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200 mb-8">
                <div className="flex gap-8">
                  {['overview', 'features', 'specifications', 'financing', 'trade-in'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`pb-4 font-medium capitalize transition-colors cursor-pointer whitespace-nowrap ${
                        activeTab === tab
                          ? 'text-[#2563EB] border-b-2 border-[#2563EB]'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {tab === 'trade-in' ? 'Trade-In' : tab}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              {activeTab === 'overview' && (
                <div className="space-y-8">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      Vehicle Description
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {vehicle.description}
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      Key Information
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { label: 'VIN', value: vehicle.vin },
                        { label: 'Stock Number', value: vehicle.stockNumber },
                        {
                          label: 'Exterior Color',
                          value: vehicle.exteriorColor,
                        },
                        {
                          label: 'Interior Color',
                          value: vehicle.interiorColor,
                        },
                        { label: 'Transmission', value: vehicle.transmission },
                        { label: 'Drivetrain', value: vehicle.drivetrain },
                      ].map((item, index) => (
                        <div
                          key={index}
                          className="bg-gray-50 rounded-xl p-4"
                        >
                          <p className="text-sm text-gray-600 mb-1">
                            {item.label}
                          </p>
                          <p className="font-semibold text-gray-900">
                            {item.value}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'features' && (
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-6">
                    Standard Features
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {vehicle.features?.map((feature, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3"
                      >
                        <div className="w-6 h-6 flex items-center justify-center bg-[#2563EB]/10 rounded-full flex-shrink-0">
                          <i className="ri-check-line text-[#2563EB] text-sm"></i>
                        </div>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'specifications' && (
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-6">
                    Technical Specifications
                  </h3>
                  <div className="space-y-4">
                    {[
                      { label: 'Engine', value: vehicle.engine },
                      {
                        label: 'Transmission',
                        value: vehicle.transmission,
                      },
                      { label: 'Drivetrain', value: vehicle.drivetrain },
                      { label: 'Fuel Type', value: vehicle.fuelType },
                      { label: 'MPG City', value: vehicle.mpgCity },
                      { label: 'MPG Highway', value: vehicle.mpgHighway },
                      {
                        label: 'Exterior Color',
                        value: vehicle.exteriorColor,
                      },
                      {
                        label: 'Interior Color',
                        value: vehicle.interiorColor,
                      },
                    ].map((spec, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between py-4 border-b border-gray-200"
                      >
                        <span className="text-gray-600">{spec.label}</span>
                        <span className="font-semibold text-gray-900">
                          {spec.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'financing' && (
                <div className="space-y-8">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Payment Calculator
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Estimate your monthly payments based on your preferences
                    </p>
                  </div>

                  {/* Monthly Payment Display */}
                  <div className="bg-gradient-to-br from-[#2563EB] to-[#1d4ed8] rounded-2xl p-8 text-white">
                    <p className="text-sm opacity-90 mb-2">Estimated Monthly Payment</p>
                    <p className="text-5xl font-bold mb-4">
                      ${monthlyPayment.toFixed(0)}
                      <span className="text-2xl font-normal opacity-90">/mo</span>
                    </p>
                    <p className="text-sm opacity-80">
                      Based on {loanTerm} month term at {interestRate}% APR
                    </p>
                  </div>

                  {/* Calculator Controls */}
                  <div className="space-y-6">
                    {/* Down Payment */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="text-sm font-semibold text-gray-900">
                          Down Payment
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={downPayment}
                            onChange={(e) => setDownPayment(Number(e.target.value))}
                            className="w-28 px-3 py-2 text-sm border border-gray-300 rounded-lg text-right font-semibold"
                            min="0"
                            max={vehicle.price}
                            step="500"
                          />
                          <span className="text-sm text-gray-600">
                            ({((downPayment / vehicle.price) * 100).toFixed(0)}%)
                          </span>
                        </div>
                      </div>
                      <input
                        type="range"
                        value={downPayment}
                        onChange={(e) => setDownPayment(Number(e.target.value))}
                        min="0"
                        max={vehicle.price}
                        step="500"
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#2563EB]"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>$0</span>
                        <span>${vehicle.price.toLocaleString()}</span>
                      </div>
                    </div>

                    {/* Interest Rate */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="text-sm font-semibold text-gray-900">
                          Interest Rate (APR)
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={interestRate}
                            onChange={(e) => setInterestRate(Number(e.target.value))}
                            className="w-20 px-3 py-2 text-sm border border-gray-300 rounded-lg text-right font-semibold"
                            min="0"
                            max="20"
                            step="0.1"
                          />
                          <span className="text-sm text-gray-600">%</span>
                        </div>
                      </div>
                      <input
                        type="range"
                        value={interestRate}
                        onChange={(e) => setInterestRate(Number(e.target.value))}
                        min="0"
                        max="20"
                        step="0.1"
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#2563EB]"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>0%</span>
                        <span>20%</span>
                      </div>
                    </div>

                    {/* Loan Term */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="text-sm font-semibold text-gray-900">
                          Loan Term
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={loanTerm}
                            onChange={(e) => setLoanTerm(Number(e.target.value))}
                            className="w-20 px-3 py-2 text-sm border border-gray-300 rounded-lg text-right font-semibold"
                            min="12"
                            max="84"
                            step="12"
                          />
                          <span className="text-sm text-gray-600">months</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        {[36, 48, 60, 72].map((term) => (
                          <button
                            key={term}
                            onClick={() => setLoanTerm(term)}
                            className={`py-3 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap ${
                              loanTerm === term
                                ? 'bg-[#2563EB] text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {term} mo
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Payment Breakdown */}
                  <div className="bg-gray-50 rounded-2xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Payment Breakdown</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between py-2">
                        <span className="text-gray-600">Vehicle Price</span>
                        <span className="font-semibold text-gray-900">
                          ${vehicle.price.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <span className="text-gray-600">Down Payment</span>
                        <span className="font-semibold text-gray-900">
                          -${downPayment.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-t border-gray-300">
                        <span className="text-gray-600">Amount Financed</span>
                        <span className="font-semibold text-gray-900">
                          ${(vehicle.price - downPayment).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <span className="text-gray-600">Total Interest</span>
                        <span className="font-semibold text-gray-900">
                          ${(monthlyPayment * loanTerm - (vehicle.price - downPayment)).toFixed(0).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-t-2 border-gray-300">
                        <span className="font-bold text-gray-900">Total Cost</span>
                        <span className="font-bold text-gray-900 text-lg">
                          ${(monthlyPayment * loanTerm + downPayment).toFixed(0).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Disclaimer */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-information-line text-[#2563EB]"></i>
                      </div>
                      <p className="text-sm text-gray-700">
                        This calculator provides estimates only. Actual rates and terms may vary based on credit approval, vehicle condition, and lender requirements. Contact us for personalized financing options.
                      </p>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <button className="w-full py-4 bg-[#2563EB] text-white rounded-full font-medium hover:bg-[#1d4ed8] transition-colors cursor-pointer whitespace-nowrap">
                    Get Pre-Approved Now
                  </button>
                </div>
              )}

              {activeTab === 'trade-in' && <TradeInEstimator />}
            </div>

            {/* Right Column - Price & Actions */}
            <div className="col-span-1">
              <div className="bg-gray-50 rounded-2xl p-8 sticky top-24">
                <div className="mb-6">
                  <p className="text-sm text-gray-600 mb-2">Price</p>
                  <p className="text-4xl font-bold text-gray-900">
                    ${vehicle.price.toLocaleString()}
                  </p>
                </div>

                <div className="space-y-3 mb-6">
                  <button className="w-full py-4 bg-[#2563EB] text-white rounded-full font-medium hover:bg-[#1d4ed8] transition-colors cursor-pointer whitespace-nowrap">
                    Schedule Test Drive
                  </button>
                  <button className="w-full py-4 bg-white border-2 border-gray-300 text-gray-900 rounded-full font-medium hover:border-gray-400 transition-colors cursor-pointer whitespace-nowrap">
                    Get Pre-Approved
                  </button>
                  <button className="w-full py-4 bg-white border-2 border-gray-300 text-gray-900 rounded-full font-medium hover:border-gray-400 transition-colors cursor-pointer whitespace-nowrap">
                    Make an Offer
                  </button>
                </div>

                <div className="border-t border-gray-300 pt-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#2563EB]/10 rounded-full">
                      <i className="ri-phone-line text-[#2563EB]"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Call Us</p>
                      <p className="font-semibold text-gray-900">
                        (404) 852-7336
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#2563EB]/10 rounded-full">
                      <i className="ri-mail-line text-[#2563EB]"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email Us</p>
                      <p className="font-semibold text-gray-900">
                        icargamail@gmail.com
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <div className="relative w-full h-full flex items-center justify-center p-4">
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center cursor-pointer transition-colors z-10"
            >
              <i className="ri-close-line text-white text-2xl"></i>
            </button>

            {/* Image Counter */}
            <div className="absolute top-6 left-6 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full z-10">
              <p className="text-white text-sm font-medium">
                {currentImageIndex + 1} / {vehicle.images.length}
              </p>
            </div>

            {/* Main Image */}
            <img
              src={vehicle.images[currentImageIndex]}
              alt={`${vehicle.name} - Image ${currentImageIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Navigation Arrows */}
            {vehicle.images.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    lightboxPrevImage();
                  }}
                  className="absolute left-6 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center cursor-pointer transition-colors"
                >
                  <i className="ri-arrow-left-s-line text-white text-3xl"></i>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    lightboxNextImage();
                  }}
                  className="absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center cursor-pointer transition-colors"
                >
                  <i className="ri-arrow-right-s-line text-white text-3xl"></i>
                </button>
              </>
            )}

            {/* Thumbnail Strip */}
            {vehicle.images.length > 1 && (
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 bg-white/10 backdrop-blur-sm p-2 rounded-xl max-w-[90vw] overflow-x-auto">
                {vehicle.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImageIndex(index);
                    }}
                    className={`w-16 h-16 rounded-lg overflow-hidden cursor-pointer transition-all flex-shrink-0 ${
                      index === currentImageIndex
                        ? 'ring-2 ring-white scale-110'
                        : 'opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={img}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
